import React, { useState, useEffect } from 'react';
import './AttendanceForm.css';
import { useLocation, useNavigate } from 'react-router-dom';
import pidilite_logo from "../../Assets/Images/pidilite_logo.png";
import CryptoJS from 'crypto-js';
import { getNodeServerUrl } from '../../utils/getNodeUrl';
import { FaPlus, FaTrash } from 'react-icons/fa';

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

function decryptParam(encryptedData, key) {
    try {
        const decoded = decodeURIComponent(encryptedData);
        let bytes = CryptoJS.AES.decrypt(decoded, key);
        let decrypted = bytes.toString(CryptoJS.enc.Utf8);
        return decrypted;
    } catch (error) {
        try {
            let bytes = CryptoJS.AES.decrypt(encryptedData, key);
            let decrypted = bytes.toString(CryptoJS.enc.Utf8);
            return decrypted;
        } catch (retryError) {
            return '';
        }
    }
}

function AttendanceForm() {
    const query = useQuery();
    const navigate = useNavigate();
    const secretKey = 'ThisIsASecretKey';
    
    const [meetingId, setMeetingId] = useState('');
    const [dealerCode, setDealerCode] = useState('');
    const [participantName, setParticipantName] = useState('');
    const [noOfParticipants, setNoOfParticipants] = useState(1);
    const [participantDetails, setParticipantDetails] = useState([
        { name: '', relationship: '' }
    ]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        const encryptedId = query.get('id');
        const dealerCodeParam = query.get('dealer');
        
        if (encryptedId) {
            try {
                const decryptedId = decryptParam(encryptedId, secretKey);
                
                if (decryptedId && decryptedId.trim()) {
                    setMeetingId(decryptedId);
                    setError('');
                } else {
                    setError('Invalid QR code. Failed to decrypt meeting ID. Please scan again.');
                }
            } catch (err) {
                setError('Invalid QR code. Failed to decrypt meeting ID. Please scan again.');
            }
        } else {
            setError('Invalid QR code. Missing meeting ID. Please scan again.');
        }

        if (dealerCodeParam) {
            setDealerCode(dealerCodeParam);
        }
    }, [query]);

    // Update participant rows when number changes
    useEffect(() => {
        const count = parseInt(noOfParticipants) || 0;
        if (count > 0) {
            const newDetails = Array.from({ length: count }, (_, i) => 
                participantDetails[i] || { name: '', relationship: '' }
            );
            setParticipantDetails(newDetails);
        } else {
            setParticipantDetails([{ name: '', relationship: '' }]);
        }
    }, [noOfParticipants]);

    const handleParticipantChange = (index, field, value) => {
        const updatedDetails = [...participantDetails];
        updatedDetails[index][field] = value;
        setParticipantDetails(updatedDetails);
    };

    const addParticipantRow = () => {
        setParticipantDetails([...participantDetails, { name: '', relationship: '' }]);
        setNoOfParticipants(participantDetails.length + 1);
    };

    const removeParticipantRow = (index) => {
        if (participantDetails.length > 1) {
            const updatedDetails = participantDetails.filter((_, i) => i !== index);
            setParticipantDetails(updatedDetails);
            setNoOfParticipants(updatedDetails.length);
        }
    };

    const validateForm = () => {
        if (!meetingId) {
            setError('Meeting ID is missing. Please scan the QR code again.');
            return false;
        }
        if (!dealerCode.trim()) {
            setError('Please enter Dealer Code');
            return false;
        }
        if (!participantName.trim()) {
            setError('Please enter your name');
            return false;
        }
        if (!noOfParticipants || noOfParticipants < 1) {
            setError('Number of participants must be at least 1');
            return false;
        }
        
        // Validate each participant detail
        for (let i = 0; i < participantDetails.length; i++) {
            if (!participantDetails[i].name.trim()) {
                setError(`Please enter name for participant ${i + 1}`);
                return false;
            }
            if (!participantDetails[i].relationship.trim()) {
                setError(`Please enter relationship for participant ${i + 1}`);
                return false;
            }
        }
        
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${getNodeServerUrl()}/DRM_Attendance/markAttendance`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    meetingId,
                    dealerCode: dealerCode.trim(),
                    participantName: participantName.trim(),
                    noOfParticipants: parseInt(noOfParticipants),
                    participantDetails
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccessMessage('Attendance marked successfully! Redirecting to feedback form...');
                
                // Auto-redirect to feedback form after 2 seconds
                setTimeout(() => {
                    const reEncryptedId = CryptoJS.AES.encrypt(meetingId, secretKey).toString();
                    navigate(`/DRMFeedbackForm?id=${encodeURIComponent(reEncryptedId)}&dealer=${encodeURIComponent(dealerCode.trim())}`);
                }, 2000);
            } else {
                if (response.status === 409) {
                    setError('Attendance already marked for this dealer at this meeting.');
                } else {
                    setError(data.message || 'Failed to mark attendance. Please try again.');
                }
            }
        } catch (err) {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="attendance-form-container">
            <div className="attendance-form-header">
                <img src={pidilite_logo} alt="Pidilite Logo" className="attendance-form-logo" />
            </div>
            
            <div className="attendance-form-content">
                <h1 className="attendance-form-title">Mark Attendance</h1>
                <p className="attendance-form-subtitle">Please fill in the details below</p>
                
                {error && <div className="error-message">{error}</div>}
                {successMessage && <div className="success-message">{successMessage}</div>}
                
                <form onSubmit={handleSubmit} className="attendance-form">
                    <div className="form-group">
                        <label htmlFor="dealerCode">Dealer Code <span className="required">*</span></label>
                        <input
                            type="text"
                            id="dealerCode"
                            className="form-input"
                            value={dealerCode}
                            onChange={(e) => setDealerCode(e.target.value)}
                            disabled={loading || !!successMessage}
                            placeholder="Enter dealer code"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="participantName">Your Name <span className="required">*</span></label>
                        <input
                            type="text"
                            id="participantName"
                            className="form-input"
                            value={participantName}
                            onChange={(e) => setParticipantName(e.target.value)}
                            disabled={loading || !!successMessage}
                            placeholder="Enter your name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="noOfParticipants">Number of Participants <span className="required">*</span></label>
                        <input
                            type="number"
                            id="noOfParticipants"
                            className="form-input"
                            value={noOfParticipants}
                            onChange={(e) => setNoOfParticipants(e.target.value)}
                            disabled={loading || !!successMessage}
                            min="1"
                            placeholder="Enter number"
                        />
                    </div>

                    <div className="participants-section">
                        <div className="participants-header">
                            <h3>Participant Details</h3>
                            <button
                                type="button"
                                className="add-participant-btn"
                                onClick={addParticipantRow}
                                disabled={loading || !!successMessage}
                            >
                                <FaPlus /> Add Participant
                            </button>
                        </div>

                        {participantDetails.map((participant, index) => (
                            <div key={index} className="participant-row">
                                <div className="participant-row-header">
                                    <span className="participant-number">Participant {index + 1}</span>
                                    {participantDetails.length > 1 && (
                                        <button
                                            type="button"
                                            className="remove-participant-btn"
                                            onClick={() => removeParticipantRow(index)}
                                            disabled={loading || !!successMessage}
                                        >
                                            <FaTrash />
                                        </button>
                                    )}
                                </div>
                                <div className="participant-fields">
                                    <div className="form-group">
                                        <label htmlFor={`name-${index}`}>Name <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id={`name-${index}`}
                                            className="form-input"
                                            value={participant.name}
                                            onChange={(e) => handleParticipantChange(index, 'name', e.target.value)}
                                            disabled={loading || !!successMessage}
                                            placeholder="Enter name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor={`relationship-${index}`}>Relationship <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id={`relationship-${index}`}
                                            className="form-input"
                                            value={participant.relationship}
                                            onChange={(e) => handleParticipantChange(index, 'relationship', e.target.value)}
                                            disabled={loading || !!successMessage}
                                            placeholder="e.g., Owner, Manager, Staff"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={loading || !!successMessage}
                    >
                        {loading ? 'Submitting...' : 'Submit Attendance'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default AttendanceForm;
